/**
 * Preferencias de interfaz por usuario (columnas visibles, contactos por
 * página, tarjetas del embudo…). Se leen y se guardan en la tabla
 * `user_preferences` (única por user_id + organization_id + key), con RLS
 * por usuario. La lectura es síncrona desde una caché en memoria para no
 * cambiar ningún componente.
 */

import { supabase } from './supabaseClient'

export interface PipelineCardPreferences {
  mode: 'compacto' | 'ampliado'
  fields: string[]
  showSource: boolean
}

export interface UserPreferences {
  leadColumns: string[]
  /** Contactos por página en la tabla de Leads (`leads_per_page`). */
  leadsPerPage: number
  pipelineCards: PipelineCardPreferences
}

/** Opciones del selector «Contactos por página». */
export const LEADS_PER_PAGE_OPTIONS = [10, 20, 30, 50]

export const DEFAULT_LEADS_PER_PAGE = 10

export const DEFAULT_LEAD_COLUMNS = [
  'name',
  'company',
  'phone',
  'email',
  'city',
  'service',
  'budget',
  'source',
  'stage',
  'createdAt',
  'note',
]

export const DEFAULT_PIPELINE_CARDS: PipelineCardPreferences = {
  mode: 'compacto',
  fields: ['company', 'phone', 'date'],
  showSource: false,
}

/** Clave de cada preferencia dentro de `user_preferences`. */
const KEYS: Record<keyof UserPreferences, string> = {
  leadColumns: 'lead_columns',
  leadsPerPage: 'leads_per_page',
  pipelineCards: 'pipeline_cards',
}

function defaults(): UserPreferences {
  return {
    leadColumns: [...DEFAULT_LEAD_COLUMNS],
    leadsPerPage: DEFAULT_LEADS_PER_PAGE,
    pipelineCards: {
      ...DEFAULT_PIPELINE_CARDS,
      fields: [...DEFAULT_PIPELINE_CARDS.fields],
    },
  }
}

const store = new Map<string, UserPreferences>()
const listeners = new Set<() => void>()
let organizationId = ''

export function getPreferences(userId: string): UserPreferences {
  if (!store.has(userId)) store.set(userId, defaults())
  return store.get(userId) as UserPreferences
}

export function setPreferences(
  userId: string,
  patch: Partial<UserPreferences>,
): void {
  const next = { ...getPreferences(userId), ...patch }
  store.set(userId, next)
  listeners.forEach((listener) => listener())

  // Persistencia en Supabase (una fila por preferencia).
  if (!organizationId) return
  void Promise.all(
    (Object.keys(patch) as (keyof UserPreferences)[]).map((key) =>
      supabase.from('user_preferences').upsert(
        {
          user_id: userId,
          organization_id: organizationId,
          key: KEYS[key],
          value: next[key],
        },
        { onConflict: 'user_id,organization_id,key' },
      ),
    ),
  )
}

/** Carga las preferencias guardadas del usuario al iniciar sesión. */
export async function loadPreferences(
  userId: string,
  organization: string,
): Promise<void> {
  organizationId = organization
  const { data } = await supabase
    .from('user_preferences')
    .select('key, value')
    .eq('user_id', userId)
    .eq('organization_id', organization)

  const next = defaults()
  ;(data ?? []).forEach((row: { key: string; value: unknown }) => {
    if (row.key === KEYS.leadColumns && Array.isArray(row.value))
      next.leadColumns = row.value as string[]
    if (row.key === KEYS.leadsPerPage && typeof row.value === 'number')
      next.leadsPerPage = row.value
    if (row.key === KEYS.pipelineCards && row.value)
      next.pipelineCards = row.value as PipelineCardPreferences
  })
  store.set(userId, next)
  listeners.forEach((listener) => listener())
}

export function subscribePreferences(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
